import 'babel-polyfill';
import 'react-notifications/lib/notifications.css';
import 'src/scss/main.scss';

import React, { Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { NotificationContainer } from 'react-notifications';
import {
  Route,
  BrowserRouter as Router,
  Routes,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom';

import { Popup } from 'src/components/complex';
import IncomingCallPopup from 'src/components/call/popup';
import BaseComponent from 'src/components/BaseComponent';
import { getStripePromise } from 'src/helpers/stripe';

import { logger, isAdminPage, lang, permissions, getRouter } from 'src/helpers';

import model from 'src/model';

import adminAuthPages from './pages/admin/auth';
import adminMainPages from './pages/admin/main';
import authPages from './pages/auth';
import mainPages from './pages/main';
import publicPages from './pages/public';
if (isAdminPage()) permissions.switchToAdmin();

permissions.addPages(adminMainPages, {
  webType: 'admin',
  defaultCheckActive: ({ logged, isAdmin }) => logged && isAdmin
});
permissions.addPages(adminAuthPages, {
  webType: 'admin',
  defaultCheckActive: ({ logged, isAdmin }) => !logged || !isAdmin
});
permissions.addPages(mainPages, {
  webType: 'front',
  defaultCheckActive: ({ logged }) => logged
});
permissions.addPages(authPages, {
  webType: 'front',
  isAuthPage: true,
  defaultCheckActive: ({ logged, pages, page }) => {
    return (
      !logged ||
      [pages.PHONE_VERIFICATION_PAGE.key, pages.EMAIL_VERIFICATION_PAGE.key].includes(page.key)
    );
  }
});
permissions.addPages(publicPages, {
  webType: 'public'
});

const Loading = () => <div className='mc-loading'>Initializing...</div>;
const prefixUrl = isAdminPage() ? '/admin' : '';
const notRedirectUrl = [
  ...Object.values(authPages).map((r) => r.path),
  ...Object.values(publicPages).map((r) => r.path)
];

const Redirect = () => {
  let navigate = useNavigate();
  let location = useLocation();
  useEffect(() => {
    setTimeout(() => {
      navigate(location?.state?.url || '/', { state: location?.state?.params, replace: true });
    }, 200);
  }, [location, navigate]);
  return <div className='mc-loading'>Loading...</div>;
};

export class App extends BaseComponent {
  state = {
    loaded: false,
    rerenderId: 0,
    redirect: prefixUrl
  };

  load = async () => {
    logger.component('Bootstrap load');
    let redirectUrl = window.location.href.replace(window.location.origin, '');
    let redirect = prefixUrl;
    await model.start((logged) => {
      this.setLanguage(
        model.state.language ||
          (window.navigator.userLanguage || window.navigator.language).substring(0, 2).toLowerCase()
      );

      const { user } = model.state;
      if (!logged && redirectUrl && !notRedirectUrl.find((uri) => redirectUrl.includes(uri))) {
        redirect = redirectUrl;
        model.setStateSilent({ redirect });
      }

      permissions.run({
        auth: logged && user,
        logged,
        isAdmin: model.isAdmin()
      });
    });

    model.subscribe(this.subscription);
    // wait one tick so the subscription above is applied before showing the app
    setTimeout(() => {
      this.setState({
        loaded: true,
        redirect: redirect === prefixUrl + '/' ? prefixUrl : redirect
      });
    }, 0);
  };

  setLanguage = async (language) => {
    await lang.setLanguage(language);
    model.setState({ language });
  };

  componentDidMount() {
    logger.component('Bootstrap mount');
    this.paymentIntentClientSecret().then().catch();
    model.on('logout', this.onLogout);
    model.on('auth', this.onAuth);
    model.on('rerender', this.rerender);
    model.on('initLang', this.setLanguage);
    setTimeout(this.load, 1);
    setTimeout(this.lazyLoad, 1000);
    window.onfocus = () => {
      model.emit('appState:foreground', true);
    };
    window.onblur = () => {
      model.emit('appState:foreground', false);
    };
    document.documentElement.className = isAdminPage() ? 'mc-admin ' : 'mc-front ';
  }

  componentWillUnmount() {
    logger.component('Bootstrap unmount');
    model.unsubscribe(this.subscription);
    model.cancel('logout', this.onLogout);
    model.cancel('auth', this.onAuth);
    model.cancel('rerender', this.rerender);
    model.cancel('initLang', this.setLanguage);
  }

  stripePromise = undefined;

  getStripe = async () => {
    if (typeof this.stripePromise === 'undefined') {
      this.stripePromise = getStripePromise();
    }
    return this.stripePromise;
  };

  paymentIntentClientSecret = async () => {
    try {
      const clientSecret = new URLSearchParams(window.location.search).get(
        'payment_intent_client_secret'
      );
      const url = new URL(window.location);
      url.searchParams.delete('payment_intent_client_secret');
      url.searchParams.delete('payment_intent');
      window.history.replaceState({}, '', url);

      if (!clientSecret) return;
      const stripe = await this.getStripe();
      const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
      if (paymentIntent?.status === 'succeeded') {
        model.api(
          'contact/confirmPaymentIntent',
          { paymentIntentId: paymentIntent.id },
          (err, data, success) => {
            console.warn('contact/confirmPaymentIntent', { paymentIntent, err, data, success });
            model.emit('payment_result', { paymentIntent, err, data, success });
          }
        );
      }
    } catch (e) {
      console.warn('paymentIntentClientSecret error', e);
    }
  };

  rerender = () => {
    permissions.run({
      auth: model.isLoggedIn() && model.state.user,
      logged: model.isLoggedIn(),
      isAdmin: model.isAdmin()
    });

    this.setState({ rerenderId: this.state.rerenderId + 1 });
  };

  lazyLoad() {
    model.checkConfirmCookiePolicy().then();
  }

  onLogout = (redirect) => {
    permissions.run({
      auth: model.isLoggedIn() && model.state.user,
      logged: model.isLoggedIn(),
      isAdmin: model.isAdmin()
    });

    if (typeof redirect === 'string') {
      window.location.replace(redirect);
    }
    model.emit('location', lang.location);
    model.emit('initLang', lang.language);
    this.setState({ redirect: prefixUrl });
  };

  onAuth = (data) => {
    permissions.run({
      auth: model.isLoggedIn() && model.state.user,
      logged: model.isLoggedIn(),
      isAdmin: model.isAdmin()
    });

    let { redirect } = model.state;

    let r = () => {
      try {
        if ((redirect || permissions.history) && !data?.byPopup) {
          let state =
            permissions.history && permissions.history.params?.isRedirect
              ? { ...permissions.history }
              : { url: redirect, isRedirect: true, params: { isRedirect: true } };
          console.log('redirect', { state }, getRouter());
          getRouter().navigate('/redirect', { state });
          permissions.history = null;
        }
      } catch (e) {
        console.warn('redirect error', e);
      }
    };
    r();
    if (data?.byPopup) {
      this.setState({ loaded: true });
    } else this.setState({ loaded: true, rerenderId: Date.now() });
  };

  render() {
    let { loaded } = this.state;

    logger.component('Bootstrap render, state:', this.state);

    if (!loaded) {
      return <Loading />;
    }

    const activePages = permissions.ACTIVE_PAGE_LIST;

    let defaultRedirect = activePages.find((p) => p.isDrawMenu)?.path;
    if (!defaultRedirect) defaultRedirect = activePages.find((p) => !p.path.includes(':'))?.path;

    return (
      <Suspense fallback={<Loading />} key={this.state.rerenderId}>
        <Router>
          <Popup />
          <Routes>
            {activePages.map((rProps, index) => (
              <Route
                exact
                id={rProps.name}
                key={(rProps.name || rProps.key) + '_' + index}
                path={rProps.path}
                element={rProps.element}
              />
            ))}
            <Route path='/redirect' element={<Redirect />} />
            <Route path='*' element={<Navigate to={defaultRedirect} replace={true} />} />
          </Routes>
        </Router>
        <NotificationContainer />
        <IncomingCallPopup />
      </Suspense>
    );
  }
}

// ReactDOM.render(<App />, document.getElementById('root'));
const container = document.getElementById('root');
const root = createRoot(container);
root.render(<App />);
